import { useEffect, useState } from "react";
import LitLabLayout from "../components/LitLabLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, FileText, HelpCircle, Trash2, CheckCircle2, History, Eye, RotateCcw, ChevronDown, Layout, Plus, RefreshCw, ExternalLink, Info, Upload, BarChart3, TrendingUp, Mail } from "lucide-react";
import axios from "axios";
import { litlabArticles, litlabArticlePut, litlabArticleHistory, litlabArticleRevert, litlabQnaList, litlabQnaPut, litlabQnaDelete, litlabPagesList, litlabPageGet, litlabPagePut, litlabPageCreate, litlabPageDelete, litlabPageSync, litlabPageSyncAll, litlabAboutTabsGet, litlabAboutTabsPut, litlabUploadImage } from "../api";
import TipTapParagraph from "../components/TipTapParagraph";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "link", "color", "background",
];

/**
 * LitLabCms — /litlab/admin/articles (auth required)
 *
 * Two tabs the customer runs themselves:
 *   1. Services articles — every popup on /litlab/services
 *   2. Iris FAQ — every question a visitor has asked, sorted by hit-count.
 *      The admin can rewrite the answer, mark it as curated (locks the
 *      pair from LLM overwrite), or delete a bad entry.
 */
export default function LitLabCms() {
  const [tab, setTab] = useState("dashboard");
  return (
    <LitLabLayout>
      <section className="min-h-screen bg-slate-50 pt-16 pb-16" data-testid="litlab-cms">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Content management</div>
          <h1 className="mt-2 litlab-serif text-4xl font-bold text-slate-900">
            Edit your site's pop-up articles and AI answers
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Every "Learn more" pop-up on your Services page — and every
            question Iris has ever answered — lives here. Rewrite anything,
            hit Save, and the change is live in seconds. No deploy needed.
          </p>
          <div className="mt-6 flex gap-2 flex-wrap">
            <Button size="sm" variant={tab === "dashboard" ? "default" : "outline"}
              onClick={() => setTab("dashboard")} data-testid="cms-tab-dashboard"
              className={tab === "dashboard" ? "bg-sky-700 text-white" : ""}>
              <BarChart3 className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <Button size="sm" variant={tab === "articles" ? "default" : "outline"}
              onClick={() => setTab("articles")} data-testid="cms-tab-articles"
              className={tab === "articles" ? "bg-sky-700 text-white" : ""}>
              <FileText className="w-4 h-4 mr-1" /> Services articles
            </Button>
            <Button size="sm" variant={tab === "pages" ? "default" : "outline"}
              onClick={() => setTab("pages")} data-testid="cms-tab-pages"
              className={tab === "pages" ? "bg-sky-700 text-white" : ""}>
              <Layout className="w-4 h-4 mr-1" /> Service pages
            </Button>
            <Button size="sm" variant={tab === "about" ? "default" : "outline"}
              onClick={() => setTab("about")} data-testid="cms-tab-about"
              className={tab === "about" ? "bg-sky-700 text-white" : ""}>
              <Info className="w-4 h-4 mr-1" /> About tabs
            </Button>
            <Button size="sm" variant={tab === "qna" ? "default" : "outline"}
              onClick={() => setTab("qna")} data-testid="cms-tab-qna"
              className={tab === "qna" ? "bg-sky-700 text-white" : ""}>
              <HelpCircle className="w-4 h-4 mr-1" /> Iris FAQ
            </Button>
          </div>
          <div className="mt-6">
            {tab === "dashboard" && <DashboardTab />}
            {tab === "articles" && <ArticlesTab />}
            {tab === "pages" && <PagesTab />}
            {tab === "about" && <AboutTabsTab />}
            {tab === "qna" && <QnaTab />}
          </div>
        </div>
      </section>
    </LitLabLayout>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    setData(null); setErr("");
    axios.get(`${API}/litlab/cms/dashboard?days=${days}`, { headers: httpAuth() })
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setErr("Sign in as admin to see dashboard"); });
    return () => { live = false; };
  }, [days]);

  if (err) return <div className="text-sm text-slate-500">{err}</div>;
  if (!data) return <Loader />;

  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="litlab-cms-dashboard">
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Window</div>
        <div className="inline-flex rounded-lg bg-white ring-1 ring-slate-200 p-0.5">
          {[7, 30, 90, 180].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    data-testid={`cms-dash-range-${d}`}
                    className={`px-3 py-1 text-xs font-bold rounded-md ${
                      days === d ? "bg-sky-700 text-white" : "text-slate-500 hover:text-slate-900"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cms-dash-kpis">
        <Kpi icon={Mail}       label={`Quotes · last ${days}d`} value={t.quotes_window} tone="orange" />
        <Kpi icon={Mail}       label="Quotes · all time"        value={t.quotes_all_time} tone="slate" />
        <Kpi icon={Eye}        label={`Page views · ${days}d`}  value={t.views_window} tone="sky" />
        <Kpi icon={TrendingUp} label="Conversion (views → quotes)"
             value={t.conversion_pct !== null ? `${t.conversion_pct}%` : "—"} tone="emerald" />
      </div>

      <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid="cms-dash-top-pages">
        <div className="font-bold text-slate-900">Top viewed service pages</div>
        {(data.top_pages || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-3">No page-view data yet — visits will appear here.</div>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 text-left">
                <th className="py-2">Page</th>
                <th className="py-2 text-right">Views · {days}d</th>
                <th className="py-2 text-right">All time</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_pages || []).map((p) => (
                <tr key={p.slug} className="border-t border-slate-100"
                    data-testid={`cms-dash-page-${p.slug}`}>
                  <td className="py-2">
                    <a href={`/litlab/services/${p.slug}`} target="_blank" rel="noreferrer"
                       className="text-sky-700 font-medium hover:underline inline-flex items-center gap-1">
                      {p.title} <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="py-2 text-right font-bold">{p.views_window}</td>
                  <td className="py-2 text-right text-slate-500">{p.views_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid="cms-dash-recent-quotes">
        <div className="font-bold text-slate-900">Recent quotes</div>
        {(data.recent_quotes || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-3">No quotes yet.</div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {data.recent_quotes.map((q) => (
              <li key={q.id} className="py-2 flex items-center gap-3 text-sm">
                <span className="font-mono text-xs text-slate-500">#{q.reference}</span>
                <span className="font-bold text-slate-900">{q.name}</span>
                {q.company && <span className="text-slate-500">· {q.company}</span>}
                {q.service && <span className="ml-auto text-xs bg-slate-100 rounded-full px-2 py-0.5">{q.service}</span>}
                <span className="text-xs text-slate-400">{q.created_at ? new Date(q.created_at).toLocaleDateString() : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tint = {
    slate:   "bg-slate-50 text-slate-700 ring-slate-200",
    sky:     "bg-sky-50 text-sky-700 ring-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    orange:  "bg-orange-50 text-orange-700 ring-orange-200",
  }[tone] || "bg-slate-50 text-slate-700 ring-slate-200";
  return (
    <div className={`rounded-xl ring-1 p-4 ${tint}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="mt-1 text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function ArticlesTab() {
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const d = await litlabArticles();
      setRows(d.articles || []);
    } catch (e) {
      toast.error("Sign in as admin to edit articles");
    }
  };
  useEffect(() => { load(); }, []);

  if (!rows) return <Loader />;
  const tests = rows.filter((r) => r.kind === "test");
  const cats = rows.filter((r) => r.kind === "category");

  return (
    <div className="space-y-6">
      <Group title="Test methods" items={tests} onSaved={load} />
      <Group title="Capability categories" items={cats} onSaved={load} />
    </div>
  );
}

function Group({ title, items, onSaved }) {
  return (
    <div>
      <h2 className="litlab-serif text-2xl font-bold text-slate-900 mb-3">{title}</h2>
      <div className="grid gap-3">
        {items.map((a) => <ArticleRow key={a.id} a={a} onSaved={onSaved} />)}
      </div>
    </div>
  );
}

function ArticleRow({ a, onSaved }) {
  const [title, setTitle] = useState(a.title);
  const [body, setBody] = useState(a.body);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const dirty = title !== a.title || body !== a.body;

  const save = async () => {
    setBusy(true);
    try {
      await litlabArticlePut(a.kind, a.slug, { title, body });
      toast.success(`Saved · ${title}`);
      setHistory(null); // force refresh next time
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const openHistory = async () => {
    setShowHistory((s) => !s);
    if (history) return;
    try {
      const d = await litlabArticleHistory(a.kind, a.slug);
      setHistory(d.revisions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load history");
    }
  };

  const revert = async (revId) => {
    if (!window.confirm("Revert this article to the selected revision? The current copy will be snapshotted first.")) return;
    setBusy(true);
    try {
      const d = await litlabArticleRevert(a.kind, a.slug, revId);
      setTitle(d.article.title);
      setBody(d.article.body);
      toast.success("Reverted — refresh the Services page to see the change.");
      setHistory(null);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 bg-white border border-sky-200" data-testid={`article-row-${a.slug}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        {a.kind} · {a.slug}
        {a.updated_by && a.updated_by !== "seed" && (
          <span className="ml-auto text-emerald-700 normal-case tracking-normal">
            edited by {a.updated_by}
          </span>
        )}
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)}
        className="mt-2 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm font-bold"
        data-testid={`article-title-${a.slug}`} />
      {!expanded ? (
        <button onClick={() => setExpanded(true)}
          data-testid={`article-expand-${a.slug}`}
          className="mt-2 w-full text-left text-xs text-sky-700 border border-dashed border-sky-300 rounded-md py-2 px-3 hover:bg-sky-50 flex items-center justify-between">
          <span>Click to open editor + live preview</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      ) : (
        <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="litlab-quill-wrap min-w-0" data-testid={`article-body-${a.slug}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Editor
            </div>
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
            />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Live preview <span className="text-slate-400 normal-case tracking-normal">— exactly what visitors see</span>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 min-h-[200px] max-w-full overflow-hidden break-words litlab-article-body text-slate-800 text-sm leading-relaxed"
                 data-testid={`article-preview-${a.slug}`}
                 dangerouslySetInnerHTML={{ __html: body || "<p class='text-slate-400 italic'>Nothing to preview yet.</p>" }} />
          </div>
        </div>
      )}
      <div className="mt-2 flex justify-between items-center gap-2">
        <Button size="sm" variant="ghost" onClick={openHistory}
          className="text-slate-600 hover:text-sky-700"
          data-testid={`article-history-${a.slug}`}>
          <History className="w-3.5 h-3.5 mr-1" /> History
        </Button>
        <Button size="sm" disabled={!dirty || busy} onClick={save}
          className="bg-sky-700 text-white hover:bg-sky-800"
          data-testid={`article-save-${a.slug}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save
        </Button>
      </div>
      {showHistory && (
        <div className="mt-3 border-t border-slate-200 pt-3" data-testid={`article-history-panel-${a.slug}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
            Last {history?.length || 0} revisions — click any to revert (current version is auto-snapshotted first)
          </div>
          {!history ? <Loader /> : history.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No revisions yet — save an edit and it'll appear here.</div>
          ) : (
            <div className="space-y-2">
              {history.map((r) => (
                <div key={r.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs flex items-start gap-2"
                     data-testid={`history-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-500">
                      {new Date(r.archived_at).toLocaleString()} · saved by <span className="font-semibold">{r.saved_by}</span>
                    </div>
                    <div className="mt-1 text-slate-700 line-clamp-2"
                         dangerouslySetInnerHTML={{ __html: r.body?.slice(0, 220) || "" }} />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => revert(r.id)} disabled={busy}
                    className="text-xs"
                    data-testid={`history-revert-${r.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Revert
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function QnaTab() {
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const d = await litlabQnaList(300);
      setRows(d.qna || []);
    } catch {
      toast.error("Sign in as admin to view the FAQ cache");
    }
  };
  useEffect(() => { load(); }, []);

  if (!rows) return <Loader />;
  if (rows.length === 0) return (
    <Card className="p-6 border-dashed bg-white text-center text-sm text-slate-500">
      No questions cached yet. As soon as visitors chat with Iris, their
      Q&A pairs will show up here for you to review.
    </Card>
  );

  return (
    <div className="space-y-3" data-testid="qna-list">
      {rows.map((q) => <QnaRow key={q.id} q={q} onSaved={load} />)}
    </div>
  );
}

function QnaRow({ q, onSaved }) {
  const [answer, setAnswer] = useState(q.answer);
  const [busy, setBusy] = useState(false);
  const dirty = answer !== q.answer;

  const save = async (curated) => {
    setBusy(true);
    try {
      await litlabQnaPut(q.id, {
        answer, ...(curated !== undefined ? { curated } : {}),
      });
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this cached answer? The next visitor who asks will get a fresh LLM reply.")) return;
    try {
      await litlabQnaDelete(q.id);
      toast.success("Deleted");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="p-4 bg-white border border-sky-200" data-testid={`qna-row-${q.id.slice(0,10)}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
        <div className="text-slate-500 font-bold">
          {q.hits ?? 0} hit{(q.hits ?? 0) === 1 ? "" : "s"} · source {q.source || "llm"}
        </div>
        <div className="flex items-center gap-2">
          {q.curated && (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
              <CheckCircle2 className="w-3 h-3 mr-1" /> curated
            </Badge>
          )}
          <button onClick={remove} title="Delete"
            className="text-slate-400 hover:text-rose-600 p-1"
            data-testid={`qna-delete-${q.id.slice(0,10)}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 text-sm font-bold text-slate-900">
        Q: {q.question_original}
      </div>
      <textarea rows={4} value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        className="mt-2 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        data-testid={`qna-answer-${q.id.slice(0,10)}`} />
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="outline" disabled={busy}
          onClick={() => save(!q.curated)}
          data-testid={`qna-curate-${q.id.slice(0,10)}`}>
          {q.curated ? "Un-curate" : "Mark as curated"}
        </Button>
        <Button size="sm" disabled={!dirty || busy} onClick={() => save()}
          className="bg-sky-700 text-white hover:bg-sky-800"
          data-testid={`qna-save-${q.id.slice(0,10)}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save
        </Button>
      </div>
    </Card>
  );
}

function Loader() {
  return (
    <div className="flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}

// ==========================================================================
// Service Pages tab — list / edit / create / delete deep-content pages
// ==========================================================================
function PagesTab() {
  const [rows, setRows] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [editSlug, setEditSlug] = useState(null);

  const load = async () => {
    try {
      const d = await litlabPagesList();
      setRows(d.pages || []);
    } catch {
      toast.error("Sign in as admin to manage service pages");
    }
  };
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    if (!window.confirm("Re-sync all pages from litlab.com? Existing edits will be overwritten.")) return;
    setSyncing(true);
    try {
      const d = await litlabPageSyncAll(true);
      const ok = d.results.filter((r) => r.status === "ok").length;
      toast.success(`Synced ${ok} of ${d.total} pages from litlab.com`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const createPage = async () => {
    const slug = newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+|-+$/g, "");
    if (!slug || !newTitle.trim()) {
      toast.error("Slug and title are required");
      return;
    }
    setCreating(true);
    try {
      await litlabPageCreate({ slug, title: newTitle.trim() });
      toast.success(`Created /litlab/services/${slug}`);
      setNewSlug(""); setNewTitle("");
      await load();
      setEditSlug(slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete /litlab/services/${slug}? This cannot be undone.`)) return;
    try {
      await litlabPageDelete(slug);
      toast.success("Page deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!rows) return <Loader />;

  return (
    <div className="space-y-4" data-testid="cms-pages-tab">
      <Card className="p-4 bg-sky-50 border-sky-200">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Bulk sync from litlab.com</div>
            <div className="text-xs text-slate-600">
              Pulls the full text + images for all 17 service pages, preserving original dimensions.
            </div>
          </div>
          <Button size="sm" onClick={syncAll} disabled={syncing}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="cms-pages-syncall">
            {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync all from litlab.com
          </Button>
        </div>
      </Card>

      <Card className="p-4 bg-white border border-sky-200">
        <div className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add a new page
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
            placeholder="url-slug (e.g. weld-inspection)"
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            data-testid="cms-pages-new-slug" />
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Page title (e.g. Weld Inspection)"
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            data-testid="cms-pages-new-title" />
          <Button size="sm" onClick={createPage} disabled={creating || !newSlug || !newTitle}
            className="bg-sky-700 text-white hover:bg-sky-800"
            data-testid="cms-pages-create">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create
          </Button>
        </div>
      </Card>

      <div className="grid gap-3">
        {rows.map((p) => (
          <PageRow key={p.slug} page={p}
                    isEditing={editSlug === p.slug}
                    onEdit={() => setEditSlug(editSlug === p.slug ? null : p.slug)}
                    onSaved={load} onDelete={() => remove(p.slug)} />
        ))}
      </div>
    </div>
  );
}

function PageRow({ page, isEditing, onEdit, onSaved, onDelete }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (isEditing && !detail) {
      litlabPageGet(page.slug).then((d) => setDetail(d.page)).catch(() => {});
    }
  }, [isEditing, detail, page.slug]);

  return (
    <Card className="p-4 bg-white border border-sky-200" data-testid={`page-row-${page.slug}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            /litlab/services/{page.slug}
            {page.updated_by && page.updated_by !== "seed" && (
              <span className="ml-2 text-emerald-700 normal-case tracking-normal">
                · edited by {page.updated_by}
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-slate-900 truncate">{page.title}</div>
          {page.meta_description && (
            <div className="text-xs text-slate-500 truncate">{page.meta_description}</div>
          )}
        </div>
        <a href={`/litlab/services/${page.slug}`} target="_blank" rel="noopener noreferrer"
           className="text-xs text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
           data-testid={`page-view-${page.slug}`}>
          <ExternalLink className="w-3.5 h-3.5" /> View
        </a>
        <Button size="sm" variant="outline" onClick={onEdit}
                data-testid={`page-edit-${page.slug}`}>
          <ChevronDown className={`w-3.5 h-3.5 mr-1 transition ${isEditing ? "rotate-180" : ""}`} />
          {isEditing ? "Close" : "Edit"}
        </Button>
        <button onClick={onDelete}
                className="p-2 text-slate-400 hover:text-rose-600"
                data-testid={`page-delete-${page.slug}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {isEditing && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          {!detail ? <Loader /> : <PageEditor page={detail} onSaved={onSaved} />}
        </div>
      )}
    </Card>
  );
}

function PageEditor({ page, onSaved }) {
  const [title, setTitle] = useState(page.title);
  const [meta, setMeta] = useState(page.meta_description || "");
  const [blocks, setBlocks] = useState(page.blocks || []);
  const [busy, setBusy] = useState(false);
  const dirty = title !== page.title || meta !== (page.meta_description || "") ||
                 JSON.stringify(blocks) !== JSON.stringify(page.blocks || []);

  const save = async () => {
    setBusy(true);
    try {
      await litlabPagePut(page.slug, { title, meta_description: meta, blocks });
      toast.success(`Saved · ${title}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const updateBlock = (i, patch) => {
    setBlocks(blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  };
  const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = blocks.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setBlocks(copy);
  };
  const addBlock = (type) => {
    const b = type === "heading" ? { type, level: 2, text: "New heading" }
            : type === "paragraph" ? { type, html: "New paragraph." }
            : type === "list" ? { type, ordered: false, items: ["Item"] }
            : { type: "image", src: "", alt: "", align: "center", wrap: false };
    setBlocks([...blocks, b]);
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-2">
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm font-bold"
          data-testid={`page-title-${page.slug}`} />
        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Meta description</label>
        <input value={meta} onChange={(e) => setMeta(e.target.value)}
          maxLength={400}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          data-testid={`page-meta-${page.slug}`} />
      </div>

      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Blocks</div>
      <div className="space-y-2">
        {blocks.map((b, i) => (
          <BlockEditor key={i} block={b} index={i} total={blocks.length}
                        onChange={(patch) => updateBlock(i, patch)}
                        onRemove={() => removeBlock(i)}
                        onMove={(d) => moveBlock(i, d)} />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => addBlock("heading")}>+ Heading</Button>
        <Button size="sm" variant="outline" onClick={() => addBlock("paragraph")}>+ Paragraph</Button>
        <Button size="sm" variant="outline" onClick={() => addBlock("list")}>+ List</Button>
        <Button size="sm" variant="outline" onClick={() => addBlock("image")}>+ Image</Button>
      </div>

      <div className="flex justify-end">
        <Button size="sm" disabled={!dirty || busy} onClick={save}
          className="bg-sky-700 text-white hover:bg-sky-800"
          data-testid={`page-save-${page.slug}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save page
        </Button>
      </div>
    </div>
  );
}

function ImageUploadButton({ onUpload, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = () => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return;
      setBusy(true);
      try {
        const r = await litlabUploadImage(f);
        // Try to detect natural dimensions
        const img = new window.Image();
        img.onload = () => onUpload(r.url, img.naturalWidth, img.naturalHeight);
        img.onerror = () => onUpload(r.url);
        img.src = r.url;
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally { setBusy(false); }
    };
    inp.click();
  };
  return (
    <Button size="sm" variant="outline" onClick={pick} disabled={busy}
             data-testid={testid || "block-image-upload"}>
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
      Upload
    </Button>
  );
}

// ==========================================================================
// About tabs — editable key/value/blurb rows (1985 · Metallurgy · Nadcap · AC7006)
// ==========================================================================
function AboutTabsTab() {
  const [tabs, setTabs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    litlabAboutTabsGet().then((d) => setTabs(d.tabs || [])).catch(() => toast.error("Sign in as admin"));
  }, []);

  const update = (i, patch) => {
    setTabs(tabs.map((t, idx) => idx === i ? { ...t, ...patch } : t));
    setDirty(true);
  };
  const remove = (i) => { setTabs(tabs.filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => {
    setTabs([...(tabs || []), { key: `tab-${(tabs?.length || 0) + 1}`, label: "New tab", value: "", blurb: "" }]);
    setDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await litlabAboutTabsPut(tabs);
      toast.success("About tabs saved · live on /litlab/about");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!tabs) return <Loader />;
  return (
    <div className="space-y-3" data-testid="cms-about-tabs-tab">
      <Card className="p-4 bg-sky-50 border-sky-200 text-xs text-slate-600">
        These four "key facts" appear on <b>/litlab/about</b>. Add up to 12 tabs. Value is the big number/word (e.g. <b>1985</b>); blurb is the sentence underneath.
      </Card>
      {tabs.map((t, i) => (
        <Card key={i} className="p-3 bg-white border border-sky-200 grid grid-cols-1 md:grid-cols-[2fr_2fr_5fr_auto] gap-2 items-start"
              data-testid={`about-tab-row-${i}`}>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Label</label>
            <input value={t.label} onChange={(e) => update(i, { label: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm font-bold"
                    data-testid={`about-tab-label-${i}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Value</label>
            <input value={t.value} onChange={(e) => update(i, { value: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm font-bold text-sky-800"
                    data-testid={`about-tab-value-${i}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Blurb</label>
            <textarea value={t.blurb} rows={2}
                       onChange={(e) => update(i, { blurb: e.target.value })}
                       className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                       data-testid={`about-tab-blurb-${i}`} />
          </div>
          <button onClick={() => remove(i)} className="mt-5 text-rose-500 hover:text-rose-700 p-2"
                  data-testid={`about-tab-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </Card>
      ))}
      <div className="flex gap-2 justify-between">
        <Button size="sm" variant="outline" onClick={add} data-testid="about-tab-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add tab
        </Button>
        <Button size="sm" disabled={!dirty || busy} onClick={save}
                 className="bg-sky-700 text-white hover:bg-sky-800"
                 data-testid="about-tab-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save About tabs
        </Button>
      </div>
    </div>
  );
}

function BlockEditor({ block, index, total, onChange, onRemove, onMove }) {
  return (
    <Card className="p-2 bg-slate-50 border border-slate-200" data-testid={`block-${index}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {block.type} · #{index + 1}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0}
                  className="px-1 hover:text-sky-700 disabled:opacity-30"
                  data-testid={`block-up-${index}`}>▲</button>
          <button onClick={() => onMove(1)} disabled={index === total - 1}
                  className="px-1 hover:text-sky-700 disabled:opacity-30"
                  data-testid={`block-down-${index}`}>▼</button>
          <button onClick={onRemove} className="px-1 text-rose-500 hover:text-rose-700"
                  data-testid={`block-remove-${index}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {block.type === "heading" && (
        <div className="flex gap-2">
          <select value={block.level || 2}
                  onChange={(e) => onChange({ level: Number(e.target.value) })}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs">
            <option value={2}>H2</option>
            <option value={3}>H3</option>
            <option value={4}>H4</option>
          </select>
          <input value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })}
                  className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
                  data-testid={`block-heading-text-${index}`} />
        </div>
      )}
      {block.type === "paragraph" && (
        <TipTapParagraph html={block.html || ""}
                          onChange={(html) => onChange({ html })}
                          testid={`block-paragraph-tt-${index}`} />
      )}
      {block.type === "list" && (
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={!!block.ordered}
                   onChange={(e) => onChange({ ordered: e.target.checked })} />
            Ordered (numbered)
          </label>
          <textarea value={(block.items || []).join("\n")} rows={4}
                     onChange={(e) => onChange({ items: e.target.value.split("\n").filter((x) => x.trim()) })}
                     placeholder="One item per line"
                     className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                     data-testid={`block-list-items-${index}`} />
        </div>
      )}
      {block.type === "image" && (
        <div className="grid gap-1.5 text-sm">
          <div className="flex gap-2">
            <input value={block.src || ""} onChange={(e) => onChange({ src: e.target.value })}
                   placeholder="Image URL (or click Upload)"
                   className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
                   data-testid={`block-image-src-${index}`} />
            <ImageUploadButton onUpload={(url, w, h) => onChange({ src: url, width: w, height: h })}
                                testid={`block-image-upload-${index}`} />
          </div>
          <input value={block.alt || ""} onChange={(e) => onChange({ alt: e.target.value })}
                 placeholder="Alt text"
                 className="rounded-md border border-slate-300 px-2 py-1 text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <input type="number" value={block.width || ""}
                    onChange={(e) => onChange({ width: Number(e.target.value) || null })}
                    placeholder="Width"
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm" />
            <input type="number" value={block.height || ""}
                    onChange={(e) => onChange({ height: Number(e.target.value) || null })}
                    placeholder="Height"
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm" />
            <select value={block.align || "center"}
                    onChange={(e) => onChange({ align: e.target.value, wrap: e.target.value !== "center" })}
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                    data-testid={`block-image-align-${index}`}>
              <option value="left">Left · wrap</option>
              <option value="center">Center · block</option>
              <option value="right">Right · wrap</option>
            </select>
          </div>
          {block.src && (
            <img src={block.src} alt={block.alt}
                 style={{ maxWidth: 220, maxHeight: 140, border: "1px solid #cbd5e1", borderRadius: 4 }} />
          )}
        </div>
      )}
    </Card>
  );
}
